/* MAX sidebar: aggregate the complete filtered inventory, independent of the grid page. */
(() => {
  if (customElements.get('max-facet-tree')) return;
  const PREFIX = 'filter.p.m.custom.';
  const normalizeMake = value => String(value || '').toLowerCase().replace(/[\s-]/g, '');
  const values = value => [...new Set((Array.isArray(value) ? value : [value]).filter(v => v != null && String(v).trim()).map(String))];
  const sorted = map => [...map].sort(([a], [b]) => a.localeCompare(b, 'en', {numeric:true, sensitivity:'base'}));
  function group(rows, field) {
    const result = new Map();
    for (const row of rows) for (const value of values(row[field])) {
      if (!result.has(value)) result.set(value, []);
      result.get(value).push(row);
    }
    return result;
  }
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };
  customElements.define('max-facet-tree', class extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._serial = (this._serial || 0) + 1;
      // The fragment may be connected before its children finish parsing.
      if (document.readyState === 'loading') {
        this._ready = () => this.start();
        document.addEventListener('DOMContentLoaded', this._ready, {once:true});
      } else queueMicrotask(() => this.start());
    }
    start() {
      if (!this.isConnected || this._started) return;
      this._started = true;
      this.content = this.querySelector('[data-tree-content]');
      this.status = this.querySelector('[data-tree-status]');
      this.retry = this.querySelector('[data-tree-retry]');
      // Shopify may preserve capture-loop line breaks between encoded pairs.
      // Actual newlines inside values are URL-encoded, so these are formatting only.
      this.params = new URLSearchParams((this.dataset.query || '').replace(/[\r\n]/g, ''));
      if (!this.content || !this.status || !this.retry) return;
      this.retainUrlFilters(location.search);
      this.retry.onclick = () => this.load();
      this.load();
    }
    retainUrlFilters(search) {
      // Some Shopify metafield filters (notably Grade) affect the result but
      // are absent from results.filters. The theme updates history before
      // this queued start(), including when it reuses a prefetched fragment.
      const current = new URLSearchParams(search);
      const missing = new Set([...current.keys()].filter(key => key.startsWith('filter.') && !this.params.has(key)));
      for (const [key,value] of current) if (missing.has(key) && value && !this.params.getAll(key).includes(value)) this.params.append(key,value);
      const active = this.querySelector('[data-tree-active]');
      if (!active) return;
      for (const field of ['model','model_year','fuel_type','grade']) {
        const name = PREFIX + field;
        for (const value of this.params.getAll(name)) {
          if ([...active.querySelectorAll('input')].some(input => input.name === name && input.value === value)) continue;
          const input = node('input'); input.type = 'checkbox'; input.name = name; input.value = value;
          input.checked = true; input.tabIndex = -1; input.setAttribute('aria-hidden','true'); input.setAttribute('form','FacetsFilterForm'); active.append(input);
        }
      }
    }
    disconnectedCallback() {
      this._serial++;
      this._request?.abort();
      document.removeEventListener('DOMContentLoaded', this._ready);
      this._connected = this._started = false;
    }
    async inventory(url, controller) {
      const rows = [], seen = new Set();
      let total, pages = 1;
      for (let page = 1; page <= pages; page++) {
        url.searchParams.set('page', String(page));
        const timeout = setTimeout(() => controller.abort(), 15000);
        let data;
        try {
          const response = await fetch(url, {credentials:'same-origin', cache:'no-store', signal:controller.signal});
          if (!response.ok) throw new Error('Inventory HTTP ' + response.status);
          data = JSON.parse(await response.text());
        } finally {clearTimeout(timeout);}
        if (data.version !== 1 || data.page !== page || !Number.isInteger(data.total) || data.total < 0 || data.total > 25000 || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 || !Array.isArray(data.rows) || data.rows.length > 250) throw new Error('Invalid inventory response');
        const pageCount = Math.max(1, data.pages);
        if (page === 1) {total = data.total; pages = pageCount;}
        else if (total !== data.total || pages !== pageCount) throw new Error('Inventory changed during lookup');
        for (const row of data.rows) {
          if (!row || typeof row.id !== 'string' || !row.id || seen.has(row.id)) throw new Error('Duplicate or invalid product');
          for (const key of ['make','model','year','fuel','grade','displacement']) {
            const raw = Array.isArray(row[key]) ? row[key] : [row[key]];
            if (raw.some(v => v != null && typeof v !== 'string' && typeof v !== 'number')) throw new Error('Invalid facet value');
          }
          seen.add(row.id);
          rows.push(row);
        }
      }
      if (seen.size !== total) throw new Error('Incomplete inventory response');
      return rows;
    }
    async load() {
      this._request?.abort();
      const controller = this._request = new AbortController();
      const serial = ++this._serial;
      this.setAttribute('aria-busy','true');
      this.status.hidden = false;
      this.status.textContent = 'Loading models…';
      this.retry.hidden = true;
      this.content.replaceChildren();
      try {
        const url = new URL(this.dataset.collection, location.origin);
        const isSearch = this.dataset.search === 'true';
        const validPath = isSearch ? /\/search\/?$/.test(url.pathname) : /\/collections\//.test(url.pathname);
        if (url.origin !== location.origin || !validPath) throw new Error('Invalid inventory source');
        url.search = this.params.toString();
        ['page','section_id','sections','view'].forEach(key => url.searchParams.delete(key));
        // One selected make per tree, with every other active filter retained.
        url.searchParams.set(PREFIX + 'make', this.dataset.make);
        url.searchParams.set('view','max-facet-tree');
        if (isSearch) {
          // Keep the full search term, including ampersands and quotes, on every page.
          if (!url.searchParams.has('q')) throw new Error('Missing search term');
          url.searchParams.set('type','product');
          url.searchParams.set('options[prefix]','last');
          url.searchParams.set('sort_by','relevance');
        } else url.searchParams.set('sort_by','created-ascending');
        const rows = await this.inventory(url, controller);
        if (serial !== this._serial || !this.isConnected) return;
        if (rows.some(row => !values(row.make).some(make => normalizeMake(make) === normalizeMake(this.dataset.make)))) throw new Error('Unexpected make in response');
        this.render(rows);
        const missing = rows.filter(row => !values(row.model).length).length;
        this.status.textContent = missing ? missing + ' vehicles have no model information.' : rows.length ? '' : 'No matching models.';
        this.status.hidden = !this.status.textContent;
      } catch (error) {
        if (serial !== this._serial || !this.isConnected) return;
        this.status.textContent = 'Could not load all models. Please retry.';
        this.status.hidden = false;
        this.retry.hidden = false;
      } finally {
        if (serial === this._serial && this.isConnected) this.setAttribute('aria-busy','false');
      }
    }
    checkbox(field, value, labelText, count, depth, id, gradeValues) {
      const li = node('li','facet-options-list-item');
      const component = node(gradeValues ? 'grade-group-facet' : 'url-facet-checkbox');
      const span = node('span','checkbox-facet');
      const label = node('label','checkbox-facet-label');
      label.htmlFor = id;
      label.style.paddingLeft = (depth * 5) + 'px';
      const start = node('span','checkbox-facet-start');
      const input = node('input');
      input.type = 'checkbox'; input.id = id;
      input.setAttribute('aria-label',labelText);
      if (gradeValues) input.className = 'grade-group-master';
      else {
        input.name = PREFIX + field; input.value = value;
        input.setAttribute('form','FacetsFilterForm');
        input.checked = this.params.getAll(input.name).includes(value);
      }
      const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
      for (const [key,val] of Object.entries({width:'24',height:'24',viewBox:'0 0 24 24',fill:'currentColor','aria-hidden':'true'})) svg.setAttribute(key,val);
      const use = document.createElementNS('http://www.w3.org/2000/svg','use');
      use.setAttribute('href','#facet-check-icon'); svg.append(use);
      start.append(input,node('span','checkbox-facet-check'),svg,node('span','checkbox-facet-label-text',labelText));
      label.append(start);
      if (this.dataset.counts === 'true') label.append(node('span','checkbox-facet-label-count',String(count)));
      span.append(label); component.append(span);
      if (gradeValues) {
        const mirrors = node('span','grade-group-mirror'); mirrors.hidden = true;
        for (const grade of gradeValues) {
          const box = node('input'); box.type = 'checkbox'; box.name = PREFIX + 'grade'; box.value = grade;
          box.setAttribute('form','FacetsFilterForm'); box.tabIndex = -1; box.setAttribute('aria-hidden','true');
          box.checked = this.params.getAll(box.name).includes(grade); mirrors.append(box);
        }
        component.append(mirrors);
      }
      li.append(component);
      return li;
    }
    render(rows) {
      const root = this.tree(rows, 'model', 1, this.dataset.key);
      this.content.replaceChildren(root);
      // Keep active selections available to FormData while loading, then let the
      // rendered controls own those values (especially grade-group unchecking).
      const rendered = [...this.content.querySelectorAll('input[name]')];
      this.querySelectorAll('[data-tree-active] input').forEach(box => {
        if (rendered.some(input => input.name === box.name && input.value === box.value)) box.remove();
      });
    }
    tree(rows, field, depth, path) {
      const groups = group(rows, field);
      const entries = sorted(groups);
      if (field === 'year') entries.reverse();
      const list = node('ul','facet-options-list facet-options-list--nested');
      entries.forEach(([value,members], index) => {
        const nextPath = path + '-' + (index + 1);
        let text = value;
        if (field === 'fuel') {
          const displacements = new Set(members.flatMap(row => values(row.displacement)).map(v => v.replace(/,|cc/gi,'').trim()).filter(Boolean));
          const cc = Number([...displacements][0]);
          if (displacements.size === 1 && cc > 0) text += ' ' + (Math.round(cc / 100) / 10);
        }
        const paramField = {model:'model',year:'model_year',fuel:'fuel_type'}[field];
        const li = this.checkbox(paramField,value,text,members.length,depth,'filter-' + field + '-' + nextPath);
        // Preserve the existing drilldown: expand the next level only after narrowing to one option.
        if (entries.length === 1) {
          if (field === 'model') li.append(this.tree(members,'year',2,nextPath));
          else if (field === 'year') li.append(this.tree(members,'fuel',3,nextPath));
          else li.append(this.grades(members,nextPath));
        }
        list.append(li);
      });
      if (field !== 'model') this.missingInfo(list, rows, field, depth);
      return list;
    }
    missingInfo(list, rows, field, depth) {
      const count = rows.filter(row => !values(row[field]).length).length;
      if (!count) return;
      const li = node('li','facet-options-list-item');
      li.dataset.treeMissing = field;
      const label = node('span','checkbox-facet-label');
      label.style.paddingLeft = (depth * 5) + 'px';
      label.style.opacity = '.65';
      label.append(node('span','checkbox-facet-label-text', {year:'Model year',fuel:'Fuel type',grade:'Grade'}[field] + ' not listed'));
      if (this.dataset.counts === 'true') label.append(node('span','checkbox-facet-label-count',String(count)));
      li.append(label); list.append(li);
    }
    grades(rows, path) {
      const exact = group(rows,'grade'), merged = new Map();
      for (const [value,members] of exact) {
        const key = value.toLowerCase().replaceAll('seaters','seater');
        if (!merged.has(key)) merged.set(key,[]);
        merged.get(key).push([value,members]);
      }
      const list = node('ul','facet-options-list facet-options-list--nested');
      sorted(merged).forEach(([,variants],index) => {
        variants.sort((a,b) => b[1].length-a[1].length || a[0].localeCompare(b[0]));
        const count = new Set(variants.flatMap(([,members])=>members.map(row=>row.id))).size;
        const canonical = variants[0][0];
        list.append(this.checkbox('grade',canonical,canonical,count,4,'filter-grade-'+path+'-'+(index+1),variants.map(([value])=>value)));
      });
      this.missingInfo(list, rows, 'grade', 4);
      return list;
    }
  });
})();
